import { CurriculumAdapter, LevelLite, LevelStatus } from './types';
import {
  levels as winterLevels,
  getLevelStatus as winterGetLevelStatus,
  getShortLevelName as winterGetShortLevelName,
} from '../../../data/curricula/musicWinter2026/levels';
import { getMissionActivity as winterGetMissionActivity } from '../../../data/curricula/musicWinter2026/activities';

type MissionGoalType = 'dailyAchievement' | 'teacherApproved';

export interface MissionGoal {
  id: string;
  type: MissionGoalType;
  text: string; // long description
  shortText?: string; // short label for UI
}

export interface MusicWinterMission {
  id: string;
  text: string;
  type: string;
  gameId?: string;
  goals: MissionGoal[];
}

export interface MusicWinterLevel extends LevelLite {
  missions?: MusicWinterMission[];
  sideQuests?: MusicWinterMission[];
}

const mapMissionToMusicWinter = (mission: any): MusicWinterMission => {
  // If the mission already has custom goals (from the Winter 2026 curriculum data), preserve them.
  if (Array.isArray(mission?.goals)) {
    return mission as MusicWinterMission;
  }

  const goals: MissionGoal[] = [];

  // Custom goals for Beat Decoders in Rhythm Level 2
  if (mission.id === 'beat-decoders-2') {
    goals.push(
      {
        id: 'beat-decoders-2-open-once',
        type: 'dailyAchievement',
        shortText: 'Open Beat Decoders',
        text: 'Complete the daily mission of opening Beat Decoders once.',
      },
      {
        id: 'beat-decoders-2-save-song',
        type: 'dailyAchievement',
        shortText: 'Save & Name a Song',
        text: 'Save at least one song that you create in Beat Decoders and give it a name.',
      },
      {
        id: 'beat-decoders-2-share-song',
        type: 'teacherApproved',
        shortText: 'Share a Song',
        text: 'Share your saved Beat Decoders song with other students (teacher approved).',
      },
    );
  }

  return {
    id: mission.id,
    text: mission.text,
    type: mission.type,
    gameId: (mission as any).gameId,
    goals,
  };
};

const LEVELS: MusicWinterLevel[] = (winterLevels as any[]).map(level => ({
  id: level.id,
  title: level.title,
  missions: (level.missions || []).map(mapMissionToMusicWinter),
  sideQuests: (level.sideQuests || []).map(mapMissionToMusicWinter),
}));

const indexById = (id: string) => LEVELS.findIndex(l => l.id === id);

const getLevelStatus = (levelId: string, currentLevelId: string, completedIds: string[]): LevelStatus => {
  // Reuse the same status logic as the core music curriculum
  return winterGetLevelStatus(levelId, currentLevelId, completedIds) as LevelStatus;
};

const getShortLevelName = (title: string) => winterGetShortLevelName(title);

const getMissionActivity = (missionId: string) => {
  const baseActivity = winterGetMissionActivity(missionId) as any;

  // Attach tasks from the Music Winter 2026 mapping, if any exist
  const level = LEVELS.find(l =>
    [...(l.missions || []), ...(l.sideQuests || [])].some(m => m.id === missionId),
  );

  const mission =
    level && [...(level.missions || []), ...(level.sideQuests || [])].find(m => m.id === missionId);

  if (!mission) return baseActivity;

  return {
    ...baseActivity,
    goals: mission.goals,
  };
};

export const musicWinter2026Adapter: CurriculumAdapter = {
  id: 'musicWinter2026',
  levels: LEVELS,
  getLevelById: (id: string) => LEVELS.find(l => l.id === id),
  getLevelIndex: (id: string) => Math.max(0, indexById(id)),
  getLevelStatus,
  getShortLevelName,
  getMissionActivity,
};

